/**
 * Accessibility utilities for focus management, skip links, and screen reader announcements
 */

/**
 * Get all focusable elements within a container
 */
export const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(', ');

  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors)).filter(
    (element) => {
      // Check if element is visible and not hidden
      return (
        !element.hasAttribute('hidden') &&
        element.offsetParent !== null &&
        window.getComputedStyle(element).visibility !== 'hidden'
      );
    }
  );
};

/**
 * Focus trap utility - traps focus within a container
 */
export class FocusTrap {
  private container: HTMLElement;
  private firstFocusableElement: HTMLElement | null = null;
  private lastFocusableElement: HTMLElement | null = null;
  private previouslyFocusedElement: HTMLElement | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.updateFocusableElements();
  }

  private updateFocusableElements(): void {
    const focusableElements = getFocusableElements(this.container);
    this.firstFocusableElement = focusableElements[0] || null;
    this.lastFocusableElement = focusableElements[focusableElements.length - 1] || null;
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab') return;

    this.updateFocusableElements();

    if (!this.firstFocusableElement || !this.lastFocusableElement) return;

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  };

  /**
   * Activate the focus trap
   */
  activate(): void {
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    this.container.addEventListener('keydown', this.handleKeyDown);

    // Focus the first focusable element
    if (this.firstFocusableElement) {
      this.firstFocusableElement.focus();
    }
  }

  /**
   * Deactivate the focus trap and return focus to previously focused element
   */
  deactivate(): void {
    this.container.removeEventListener('keydown', this.handleKeyDown);

    // Return focus to the previously focused element
    if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
      this.previouslyFocusedElement.focus();
    }
  }
}

/**
 * Announce a message to screen readers using aria-live
 */
let announceTimeout: NodeJS.Timeout | null = null;

export const announceToScreenReader = (
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void => {
  // Clear any existing timeout
  if (announceTimeout) {
    clearTimeout(announceTimeout);
  }

  // Find or create the live region
  let liveRegion = document.getElementById('a11y-announcer');

  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.id = 'a11y-announcer';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    liveRegion.style.cssText = `
      position: absolute;
      left: -10000px;
      width: 1px;
      height: 1px;
      overflow: hidden;
    `;
    document.body.appendChild(liveRegion);
  } else {
    liveRegion.setAttribute('aria-live', priority);
  }

  // Clear the region first for repeated messages
  liveRegion.textContent = '';

  // Set the message after a brief delay to ensure it's announced
  announceTimeout = setTimeout(() => {
    if (liveRegion) {
      liveRegion.textContent = message;
    }
  }, 100);
};

/**
 * Skip link helpers - scroll to element and focus it
 */
export const skipToContent = (targetId: string): void => {
  const target = document.getElementById(targetId);

  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Make the target focusable if it's not already
    const originalTabIndex = target.getAttribute('tabindex');
    if (!originalTabIndex) {
      target.setAttribute('tabindex', '-1');
    }

    target.focus();

    // Remove tabindex after focus if it wasn't there originally
    if (!originalTabIndex) {
      target.addEventListener(
        'blur',
        () => {
          target.removeAttribute('tabindex');
        },
        { once: true }
      );
    }
  }
};

/**
 * Keyboard navigation helpers
 */

export type KeyboardKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' | 'Home' | 'End' | 'Enter' | 'Space' | 'Escape';

export interface KeyboardNavigationOptions {
  onUp?: () => void;
  onDown?: () => void;
  onLeft?: () => void;
  onRight?: () => void;
  onHome?: () => void;
  onEnd?: () => void;
  onEnter?: () => void;
  onSpace?: () => void;
  onEscape?: () => void;
  preventDefault?: boolean;
}

export const createKeyboardHandler = (
  options: KeyboardNavigationOptions
): ((event: KeyboardEvent) => void) => {
  return (event: KeyboardEvent) => {
    const { key } = event;
    const preventDefault = options.preventDefault ?? true;

    switch (key) {
      case 'ArrowUp':
        if (options.onUp) {
          if (preventDefault) event.preventDefault();
          options.onUp();
        }
        break;
      case 'ArrowDown':
        if (options.onDown) {
          if (preventDefault) event.preventDefault();
          options.onDown();
        }
        break;
      case 'ArrowLeft':
        if (options.onLeft) {
          if (preventDefault) event.preventDefault();
          options.onLeft();
        }
        break;
      case 'ArrowRight':
        if (options.onRight) {
          if (preventDefault) event.preventDefault();
          options.onRight();
        }
        break;
      case 'Home':
        if (options.onHome) {
          if (preventDefault) event.preventDefault();
          options.onHome();
        }
        break;
      case 'End':
        if (options.onEnd) {
          if (preventDefault) event.preventDefault();
          options.onEnd();
        }
        break;
      case 'Enter':
        if (options.onEnter) {
          if (preventDefault) event.preventDefault();
          options.onEnter();
        }
        break;
      case ' ':
        if (options.onSpace) {
          if (preventDefault) event.preventDefault();
          options.onSpace();
        }
        break;
      case 'Escape':
        if (options.onEscape) {
          if (preventDefault) event.preventDefault();
          options.onEscape();
        }
        break;
    }
  };
};

/**
 * Manage roving tabindex for a list of items
 */
export class RovingTabIndex {
  private items: HTMLElement[] = [];
  private currentIndex = 0;

  constructor(items: HTMLElement[], initialIndex = 0) {
    this.items = items;
    this.currentIndex = initialIndex;
    this.updateTabIndices();
  }

  private updateTabIndices(): void {
    this.items.forEach((item, index) => {
      item.setAttribute('tabindex', index === this.currentIndex ? '0' : '-1');
    });
  }

  focus(): void {
    if (this.items[this.currentIndex]) {
      this.items[this.currentIndex].focus();
    }
  }

  next(): void {
    this.currentIndex = (this.currentIndex + 1) % this.items.length;
    this.updateTabIndices();
    this.focus();
  }

  previous(): void {
    this.currentIndex = this.currentIndex === 0 ? this.items.length - 1 : this.currentIndex - 1;
    this.updateTabIndices();
    this.focus();
  }

  first(): void {
    this.currentIndex = 0;
    this.updateTabIndices();
    this.focus();
  }

  last(): void {
    this.currentIndex = this.items.length - 1;
    this.updateTabIndices();
    this.focus();
  }

  setCurrent(index: number): void {
    if (index >= 0 && index < this.items.length) {
      this.currentIndex = index;
      this.updateTabIndices();
      this.focus();
    }
  }

  updateItems(items: HTMLElement[]): void {
    this.items = items;
    this.currentIndex = Math.min(this.currentIndex, items.length - 1);
    this.updateTabIndices();
  }
}

/**
 * Check if an element is visible and focusable
 */
export const isElementFocusable = (element: HTMLElement): boolean => {
  if (element.hasAttribute('disabled')) return false;
  if (element.hasAttribute('hidden')) return false;
  if (element.getAttribute('tabindex') === '-1') return false;
  if (window.getComputedStyle(element).visibility === 'hidden') return false;
  if (window.getComputedStyle(element).display === 'none') return false;
  return true;
};

/**
 * Move focus to the first focusable element within a container
 */
export const focusFirstElement = (container: HTMLElement): void => {
  const focusableElements = getFocusableElements(container);
  if (focusableElements.length > 0) {
    focusableElements[0].focus();
  }
};

/**
 * Move focus to the last focusable element within a container
 */
export const focusLastElement = (container: HTMLElement): void => {
  const focusableElements = getFocusableElements(container);
  if (focusableElements.length > 0) {
    focusableElements[focusableElements.length - 1].focus();
  }
};

/**
 * Generate a unique ID for accessibility purposes
 */
export const generateA11yId = (prefix: string = 'a11y'): string => {
  return `${prefix}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Detect if user prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Detect if user has high contrast mode enabled
 */
export const prefersHighContrast = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-contrast: more)').matches ||
         window.matchMedia('(-ms-high-contrast: active)').matches;
};

/**
 * Get the current active element, even within shadow DOMs
 */
export const getActiveElement = (doc: Document = document): Element | null => {
  let activeElement = doc.activeElement;
  while (activeElement?.shadowRoot?.activeElement) {
    activeElement = activeElement.shadowRoot.activeElement;
  }
  return activeElement;
};

/**
 * Wait for element to be visible before focusing
 */
export const focusWhenVisible = async (
  element: HTMLElement,
  timeout: number = 5000
): Promise<boolean> => {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkVisibility = () => {
      if (Date.now() - startTime > timeout) {
        resolve(false);
        return;
      }

      if (element.offsetParent !== null) {
        element.focus();
        resolve(true);
        return;
      }

      requestAnimationFrame(checkVisibility);
    };

    checkVisibility();
  });
};

/**
 * Create an ARIA description for complex content
 */
export const createAriaDescription = (content: string | string[]): string => {
  if (Array.isArray(content)) {
    return content.join('. ');
  }
  return content;
};
